import { BACKING_CURRENCY } from '../currency';

import { REGION } from './types';

import type { FiatRailConfig } from './types';

/**
 * Fiat rail configuration — SINGLE SOURCE OF TRUTH for all fiat payment rails.
 *
 * Each entry's `sessionFlag` is an opaque string tag. Consuming apps interpret
 * flags via their own `canAccess` callback; the shared package never reads them.
 */
export const FIAT_RAIL_CONFIG = {
  ach: {
    label: 'ACH',
    currency: BACKING_CURRENCY.Usd,
    region: REGION.Us,
    sessionFlag: null,
  },
  ach_push: {
    label: 'ACH',
    currency: BACKING_CURRENCY.Usd,
    region: REGION.Us,
    sessionFlag: null,
  },
  ach_same_day: {
    label: 'ACH same day',
    currency: BACKING_CURRENCY.Usd,
    region: REGION.Us,
    sessionFlag: null,
  },
  batch_settlement: {
    label: 'Batch Settlement',
    currency: BACKING_CURRENCY.Usd,
    region: REGION.Us,
    sessionFlag: null,
  },
  bre_b: {
    label: 'Bre-B',
    currency: BACKING_CURRENCY.Cop,
    region: REGION.Other,
    sessionFlag: 'developerCanAccessCop',
  },
  co_bank_transfer: {
    label: 'CO Bank Transfer',
    currency: BACKING_CURRENCY.Cop,
    region: REGION.Other,
    sessionFlag: 'developerCanAccessCop',
  },
  faster_payments: {
    label: 'Faster Payments',
    currency: BACKING_CURRENCY.Gbp,
    region: REGION.Other,
    sessionFlag: null,
  },
  fednow: {
    label: 'FedNow',
    currency: BACKING_CURRENCY.Usd,
    region: REGION.Us,
    sessionFlag: null,
  },
  fiat_deposit_return: {
    label: 'Fiat Deposit Return',
    currency: BACKING_CURRENCY.Usd,
    region: REGION.Us,
    sessionFlag: null,
  },
  pix: {
    label: 'Pix',
    currency: BACKING_CURRENCY.Brl,
    region: REGION.Other,
    sessionFlag: null,
  },
  prefunded: {
    label: 'Prefunded',
    currency: BACKING_CURRENCY.Usd,
    region: REGION.Us,
    sessionFlag: null,
  },
  sepa: {
    label: 'SEPA',
    currency: BACKING_CURRENCY.Eur,
    region: REGION.Eur,
    sessionFlag: 'canAccessEuro',
  },
  spei: {
    label: 'SPEI',
    currency: BACKING_CURRENCY.Mxn,
    region: REGION.Other,
    sessionFlag: null,
  },
  swift: {
    label: 'SWIFT',
    currency: BACKING_CURRENCY.Usd,
    region: REGION.Eur,
    sessionFlag: 'canAccessSwift',
    // The `swift` customer endorsement is only granted to developers inside the
    // `can_use_swift_via_api` gate, which is strictly narrower than
    // `canAccessSwift` (that gate OR `developer_use_swift`). A locked row can
    // therefore never be selectable, so it is safe to surface as disabled.
    showWhenLocked: true,
  },
  wire: {
    label: 'Wire',
    currency: BACKING_CURRENCY.Usd,
    region: REGION.Us,
    sessionFlag: null,
  },
} as const satisfies Record<string, FiatRailConfig>;

/** Type union of all fiat rail IDs */
export type FiatRailId = keyof typeof FIAT_RAIL_CONFIG;

/** Type union of all fiat rail labels derived from config */
export type FiatRailLabel = (typeof FIAT_RAIL_CONFIG)[keyof typeof FIAT_RAIL_CONFIG]['label'];

/** Fiat rail values as const object, mapping PascalCase key to snake_case ID */
export const FIAT_RAIL = {
  Ach: 'ach',
  AchPush: 'ach_push',
  AchSameDay: 'ach_same_day',
  BatchSettlement: 'batch_settlement',
  BreB: 'bre_b',
  CoBankTransfer: 'co_bank_transfer',
  FasterPayments: 'faster_payments',
  FedNow: 'fednow',
  FiatDepositReturn: 'fiat_deposit_return',
  Pix: 'pix',
  Prefunded: 'prefunded',
  Sepa: 'sepa',
  Spei: 'spei',
  Swift: 'swift',
  Wire: 'wire',
} as const satisfies Record<string, FiatRailId>;
